import { AxisBottom, AxisLeft } from '@visx/axis'
import { Group } from '@visx/group'
import { scaleBand, scaleLinear } from '@visx/scale'
import { Bar } from '@visx/shape'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import {
  ChartCategoryPlan,
  ChartSegmentPlan,
  ChartTooltipDatum,
} from 'src/charting/plan/chartPlan.types'
import { ChartPlan } from 'src/charting/plan/chartPlan.types'
import { ExtendCurrency } from 'src/enums'

import * as style from './VisxStackedBarChart.style'

interface VisxStackedBarChartProps {
  plan: ChartPlan
  width?: number
  height?: number
  currency?: ExtendCurrency
  locale?: string
  showLegend?: boolean
  /**
   * Formats each x-axis tick for display. The category key stays raw (it keys
   * the band scale), so this only affects the rendered label.
   */
  xTickFormat?: (rawLabel: string) => string
  /**
   * Dotted horizontal rule at each y tick. Off by default: the Reporting
   * dashboard reads its values off the bar-top labels and was designed without
   * them, so turning these on globally would restyle five shipped panels.
   */
  showGridlines?: boolean
  /**
   * Renders bar-top totals compactly ($3.2K) instead of exactly ($3,170.00),
   * matching the y-axis ticks, which are always compact. Off by default for the
   * same reason as `showGridlines` — Reporting's panels show exact totals.
   */
  compactValueLabels?: boolean
  /**
   * Lays x-axis tick labels flat instead of at -45°. Only safe when the labels
   * are short enough not to collide at the chart's narrowest rendered width —
   * the rotation exists because Reporting's period and vendor labels do.
   */
  horizontalXLabels?: boolean
}

interface ActiveSegment {
  segment: ChartSegmentPlan
  /**
   * Whether to pop the segment's tooltip row. True when hovering a specific
   * segment; false for a column-level hover (over the amount / empty area),
   * which shows the full split without singling one row out.
   */
  highlight: boolean
  anchorX: number
  anchorY: number
  barLeftX: number
  barRightX: number
}

interface SvgPosition {
  x: number
  y: number
}

interface TooltipAnchor {
  anchorX: number
  anchorY: number
  barLeftX: number
  barRightX: number
}

interface ChartSize {
  width: number
  height: number
}

interface SegmentRect {
  y: number
  height: number
}

const margin = {
  top: 12,
  right: 16,
  bottom: 40,
  left: 56,
}

const diagonalXAxisLabelBottomMargin = 68
const diagonalXAxisLabelAngle = -45
const tooltipOffset = 16
const tooltipMinWidth = 236
const tooltipInset = 4
// Bar top-corner radius = --spacing-base-unit-1 (4px), per the chart spec.
const barCornerRadius = 4
// Target floor for a non-zero segment so present-but-tiny slices stay visible
// past the 1px divider. The floor is display-only; values and tooltips keep full
// precision, and the floor is capped per stack when space is constrained.
const targetMinSegmentHeight = 6
// Clear gap between the per-bar total label and the top of the bar
// (--spacing-base-unit-3, 12px) so the value reads as separate from the bar.
const valueLabelGap = 12
// Axis tick labels = Microcopy, ~10px (--font-size-xs), grey-500.
const axisTickFontSize = 10

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

const formatValue = (
  value: number,
  valueFormat: ChartTooltipDatum['valueFormat'],
  currency: ExtendCurrency,
  locale: string,
): string => {
  if (valueFormat === 'currencyCents') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value / 100)
  }

  return formatNumber(value)
}

const formatCompactValue = (
  value: number,
  valueFormat: ChartTooltipDatum['valueFormat'],
  currency: ExtendCurrency,
  locale: string,
): string => {
  if (valueFormat === 'currencyCents') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value / 100)
  }

  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

/**
 * Compact money with no redundant tenth — "$3.2K", "$980", "$1K".
 *
 * `formatCompactValue` above passes `maximumFractionDigits: 1`, and with compact
 * notation that PINS a decimal place on: it renders "$980.0" and "$1.0K". That
 * is what Reporting's y-axis ships today, so it is left alone; this is the
 * variant the `compactValueLabels` opt-in selects.
 *
 * Exported because a header stat shown ABOVE a chart has to be formatted
 * identically to the bars beneath it. The app's `useCurrencyFormatter` cannot
 * stand in — it defaults `maximumFractionDigits` to 2 and pins
 * `minimumFractionDigits` to match, giving "$3.17K" and "$980.00".
 */
export const formatCompactValueTrimmed = (
  value: number,
  valueFormat: ChartTooltipDatum['valueFormat'],
  currency: ExtendCurrency,
  locale: string,
): string => {
  if (valueFormat === 'currencyCents') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
    }).format(value / 100)
  }

  return new Intl.NumberFormat(locale, { notation: 'compact' }).format(value)
}

const formatTooltipValue = (
  value: ChartTooltipDatum['value'],
  valueFormat: ChartTooltipDatum['valueFormat'],
  currency: ExtendCurrency,
  locale: string,
): string => {
  if (typeof value === 'number')
    return formatValue(value, valueFormat, currency, locale)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

const getSegmentLabel = (
  segment: ChartSegmentPlan,
  valueFormat: ChartTooltipDatum['valueFormat'],
  currency: ExtendCurrency,
  locale: string,
): string =>
  `${segment.categoryKey}, ${segment.seriesLabel}: ${formatValue(
    segment.value,
    valueFormat,
    currency,
    locale,
  )}`

const getCategoryTotal = (category: ChartCategoryPlan): number =>
  category.segments.reduce((total, segment) => total + segment.value, 0)

// A period with no segments is treated as empty: no bar, no value label.
const isCategoryEmpty = (category: ChartCategoryPlan): boolean =>
  category.segments.length === 0

const compareSegmentsByVisualStackOrder = (
  a: ChartSegmentPlan,
  b: ChartSegmentPlan,
): number =>
  Math.max(b.startValue, b.endValue) - Math.max(a.startValue, a.endValue)

const getSegmentNaturalRect = (
  segment: ChartSegmentPlan,
  yScale: (value: number) => number,
): SegmentRect => {
  const yStart = yScale(segment.startValue)
  const yEnd = yScale(segment.endValue)

  return {
    y: Math.min(yStart, yEnd),
    height: Math.abs(yEnd - yStart),
  }
}

const getAdjustedHeights = (
  segments: ChartSegmentPlan[],
  yScale: (value: number) => number,
  maxStackHeight: number,
): Map<string, number> => {
  const naturalHeights = segments.map((segment) => ({
    segment,
    height: getSegmentNaturalRect(segment, yScale).height,
  }))
  const availableHeight = naturalHeights.reduce(
    (total, item) => total + item.height,
    0,
  )

  if (segments.length === 0 || availableHeight <= 0 || maxStackHeight <= 0) {
    return new Map()
  }

  const minHeight = Math.min(
    targetMinSegmentHeight,
    maxStackHeight / segments.length,
  )
  const adjustedHeights = naturalHeights.map((item) =>
    Math.max(item.height, minHeight),
  )
  const totalAdjustedHeight = adjustedHeights.reduce(
    (total, height) => total + height,
    0,
  )
  const overflow = totalAdjustedHeight - maxStackHeight

  if (overflow > 0) {
    const totalShrinkableHeight = adjustedHeights.reduce(
      (total, height) => total + Math.max(height - minHeight, 0),
      0,
    )

    if (totalShrinkableHeight > 0) {
      adjustedHeights.forEach((height, index) => {
        const shrinkableHeight = Math.max(height - minHeight, 0)
        adjustedHeights[index] =
          height - overflow * (shrinkableHeight / totalShrinkableHeight)
      })
    }
  }

  return new Map(
    naturalHeights.map(({ segment }, index) => [
      segment.id,
      adjustedHeights[index],
    ]),
  )
}

const getSegmentRenderRects = (
  segments: ChartSegmentPlan[],
  yScale: (value: number) => number,
  innerHeight: number,
): Map<string, SegmentRect> => {
  const rects = new Map<string, SegmentRect>()
  const positiveSegments = segments.filter((segment) => segment.value > 0)
  const negativeSegments = segments.filter((segment) => segment.value < 0)

  let positiveCursorY = positiveSegments.reduce((bottomY, segment) => {
    const rect = getSegmentNaturalRect(segment, yScale)
    return Math.max(bottomY, rect.y + rect.height)
  }, Number.NEGATIVE_INFINITY)
  const positiveHeights = getAdjustedHeights(
    positiveSegments,
    yScale,
    Number.isFinite(positiveCursorY) ? positiveCursorY : 0,
  )

  positiveSegments.forEach((segment) => {
    const height =
      positiveHeights.get(segment.id) ??
      getSegmentNaturalRect(segment, yScale).height
    positiveCursorY -= height
    rects.set(segment.id, { y: positiveCursorY, height })
  })

  let negativeCursorY = negativeSegments.reduce((topY, segment) => {
    const rect = getSegmentNaturalRect(segment, yScale)
    return Math.min(topY, rect.y)
  }, Number.POSITIVE_INFINITY)
  const negativeHeights = getAdjustedHeights(
    negativeSegments,
    yScale,
    Number.isFinite(negativeCursorY) ? innerHeight - negativeCursorY : 0,
  )

  negativeSegments.forEach((segment) => {
    const height =
      negativeHeights.get(segment.id) ??
      getSegmentNaturalRect(segment, yScale).height
    rects.set(segment.id, { y: negativeCursorY, height })
    negativeCursorY += height
  })

  return rects
}

const getSegmentRenderBounds = (
  rects: Map<string, SegmentRect>,
): { top: number; bottom: number; height: number } => {
  const values = Array.from(rects.values())
  const top = Math.min(...values.map((rect) => rect.y))
  const bottom = Math.max(...values.map((rect) => rect.y + rect.height))

  return {
    top,
    bottom,
    height: Math.max(bottom - top, 1),
  }
}

const VisxStackedBarChart: React.FC<VisxStackedBarChartProps> = ({
  plan,
  width = 640,
  height = 320,
  currency = ExtendCurrency.Usd,
  locale = 'en-US',
  showLegend = false,
  xTickFormat = (label) => label,
  showGridlines = false,
  compactValueLabels = false,
  horizontalXLabels = false,
}) => {
  const [activeSegment, setActiveSegment] = useState<ActiveSegment | null>(null)
  const [chartSize, setChartSize] = useState<ChartSize>({
    width: Math.max(width, 1),
    height: Math.max(height, 1),
  })
  const [tooltipSize, setTooltipSize] = useState<ChartSize>({
    width: tooltipMinWidth,
    height: 0,
  })
  const chartFrameRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const tooltipId = useId()
  const clipPathIdPrefix = useMemo(
    () => tooltipId.replace(/[^a-zA-Z0-9_-]/g, ''),
    [tooltipId],
  )
  useEffect(() => {
    const frame = chartFrameRef.current

    if (!frame || typeof ResizeObserver === 'undefined') {
      setChartSize({
        width: Math.max(width, 1),
        height: Math.max(height, 1),
      })
      return
    }

    const updateChartSize = () => {
      const rect = frame.getBoundingClientRect()
      const nextSize = {
        width: Math.max(Math.round(rect.width) || width, 1),
        height: Math.max(Math.round(rect.height) || height, 1),
      }

      setChartSize((currentSize) =>
        currentSize.width === nextSize.width &&
        currentSize.height === nextSize.height
          ? currentSize
          : nextSize,
      )
    }

    updateChartSize()

    const resizeObserver = new ResizeObserver(updateChartSize)
    resizeObserver.observe(frame)

    return () => resizeObserver.disconnect()
  }, [height, width])

  const renderedWidth = Math.max(chartSize.width, 1)
  const renderedHeight = Math.max(chartSize.height, 1)
  const innerWidth = Math.max(renderedWidth - margin.left - margin.right, 1)
  // Flat labels occupy one line, so they reclaim the gutter the -45° labels
  // need to fall into — without this the chart keeps a band of dead space under
  // it and the bars are shorter than the box allows.
  const innerHeight = Math.max(
    renderedHeight -
      margin.top -
      (horizontalXLabels ? margin.bottom : diagonalXAxisLabelBottomMargin),
    1,
  )
  const categoryLabels = useMemo(
    () => plan.categories.map((category) => category.label),
    [plan.categories],
  )

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: categoryLabels,
        range: [0, innerWidth],
        padding: 0.22,
      }),
    [categoryLabels, innerWidth],
  )

  const yScale = useMemo(() => {
    const [yMin, yMax] = plan.yDomain
    // Headroom above the tallest bar so its value label clears the plot top
    // (and the bar doesn't sit flush against the top gridline).
    const domainMax = yMax > 0 ? yMax * 1.15 : yMax
    return scaleLinear<number>({
      domain: [yMin, domainMax],
      nice: true,
      range: [innerHeight, 0],
    })
  }, [innerHeight, plan.yDomain])

  const zeroY = yScale(0)
  const activeCategoryKey = activeSegment?.segment.categoryKey
  const activeCategory = useMemo(
    () =>
      activeCategoryKey
        ? plan.categories.find((category) => category.key === activeCategoryKey)
        : undefined,
    [activeCategoryKey, plan.categories],
  )
  const tooltipSegments = useMemo(
    () =>
      activeCategory
        ? [...activeCategory.segments].sort(compareSegmentsByVisualStackOrder)
        : [],
    [activeCategory],
  )

  useLayoutEffect(() => {
    if (!activeSegment) return undefined

    const tooltip = tooltipRef.current
    if (!tooltip) return undefined

    const updateTooltipSize = () => {
      const rect = tooltip.getBoundingClientRect()
      const nextSize = {
        width: Math.max(Math.round(rect.width) || tooltipMinWidth, 1),
        height: Math.max(Math.round(rect.height) || 0, 0),
      }

      setTooltipSize((currentSize) =>
        currentSize.width === nextSize.width &&
        currentSize.height === nextSize.height
          ? currentSize
          : nextSize,
      )
    }

    updateTooltipSize()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const resizeObserver = new ResizeObserver(updateTooltipSize)
    resizeObserver.observe(tooltip)

    return () => resizeObserver.disconnect()
  }, [activeSegment, tooltipSegments])

  useEffect(() => {
    if (!activeSegment || typeof window === 'undefined') return undefined

    const clearActiveSegmentOnScroll = () => setActiveSegment(null)

    window.addEventListener('scroll', clearActiveSegmentOnScroll, true)

    return () => {
      window.removeEventListener('scroll', clearActiveSegmentOnScroll, true)
    }
  }, [activeSegment])

  const getSvgViewportPosition = useCallback(
    (x: number, y: number): SvgPosition => {
      const frame = chartFrameRef.current
      const frameRect = frame?.getBoundingClientRect()
      const svg = frame?.querySelector('svg')
      const svgRect = svg?.getBoundingClientRect()
      const svgLeft = svgRect?.left ?? frameRect?.left ?? 0
      const svgTop = svgRect?.top ?? frameRect?.top ?? 0
      const svgWidth = svgRect?.width ?? frameRect?.width ?? renderedWidth
      const svgHeight = svgRect?.height ?? frameRect?.height ?? renderedHeight

      return {
        x: svgLeft + x * (svgWidth / renderedWidth),
        y: svgTop + y * (svgHeight / renderedHeight),
      }
    },
    [renderedHeight, renderedWidth],
  )

  const getPointerPosition = useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>): SvgPosition => ({
      x: event.clientX,
      y: event.clientY,
    }),
    [],
  )

  const getCategoryTooltipPosition = useCallback(
    (
      category: ChartCategoryPlan,
      categoryX: number,
      categoryWidth: number,
      pointerPosition?: SvgPosition,
    ): TooltipAnchor => {
      const categoryTopY = getSegmentRenderBounds(
        getSegmentRenderRects(category.segments, yScale, innerHeight),
      ).top
      const leftAnchor = getSvgViewportPosition(
        margin.left + categoryX,
        margin.top + Math.max(categoryTopY, 0),
      )
      const rightAnchor = getSvgViewportPosition(
        margin.left + categoryX + categoryWidth,
        margin.top + Math.max(categoryTopY, 0),
      )

      return {
        anchorX: pointerPosition?.x ?? rightAnchor.x,
        anchorY: pointerPosition?.y ?? rightAnchor.y,
        barLeftX: leftAnchor.x,
        barRightX: rightAnchor.x,
      }
    },
    [getSvgViewportPosition, innerHeight, yScale],
  )

  const setAnchoredSegment = useCallback(
    (
      segment: ChartSegmentPlan,
      category: ChartCategoryPlan,
      categoryX: number,
      categoryWidth: number,
      pointerPosition?: SvgPosition,
      highlight = true,
    ) => {
      setActiveSegment({
        segment,
        highlight,
        ...getCategoryTooltipPosition(
          category,
          categoryX,
          categoryWidth,
          pointerPosition,
        ),
      })
    },
    [getCategoryTooltipPosition],
  )

  const clearSegmentOnMouseLeave = useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      const nextTarget = event.relatedTarget
      if (
        nextTarget instanceof Element &&
        nextTarget.closest(
          '[data-chart-stack-segment="true"], [data-chart-stack-bar="true"]',
        )
      ) {
        return
      }

      setActiveSegment(null)
    },
    [],
  )

  const tooltipPosition = useMemo(() => {
    if (!activeSegment) return undefined

    const viewportWidth =
      typeof window === 'undefined' ? renderedWidth : window.innerWidth
    const viewportHeight =
      typeof window === 'undefined' ? renderedHeight : window.innerHeight
    const tooltipWidth = Math.max(tooltipSize.width, tooltipMinWidth)
    const tooltipHeight = tooltipSize.height
    const rightPlacementLeft = Math.max(
      activeSegment.anchorX + tooltipOffset,
      activeSegment.barRightX + tooltipOffset,
    )
    const rightPlacementFits =
      rightPlacementLeft + tooltipWidth + tooltipInset <= viewportWidth
    const leftPlacementLeft = Math.min(
      activeSegment.anchorX - tooltipOffset - tooltipWidth,
      activeSegment.barLeftX - tooltipOffset - tooltipWidth,
    )
    const left = rightPlacementFits ? rightPlacementLeft : leftPlacementLeft
    const minLeft = tooltipInset
    const maxLeft = Math.max(
      minLeft,
      viewportWidth - tooltipWidth - tooltipInset,
    )
    const minTop = tooltipInset
    const maxTop = Math.max(
      minTop,
      viewportHeight - tooltipHeight - tooltipInset,
    )

    return {
      left: clamp(left, minLeft, maxLeft),
      top: clamp(activeSegment.anchorY - 12, minTop, maxTop),
    }
  }, [activeSegment, renderedHeight, renderedWidth, tooltipSize])

  return (
    <div ref={chartFrameRef} css={style.chartFrame}>
      <svg
        css={style.svg}
        role="group"
        aria-label={plan.ariaLabel}
        viewBox={`0 0 ${renderedWidth} ${renderedHeight}`}
      >
        <Group left={margin.left} top={margin.top}>
          {plan.categories.map((category) => {
            // No value label for empty / all-zero periods — the empty column
            // and its axis label already read as "no spend".
            if (isCategoryEmpty(category)) return null

            const categoryX = xScale(category.label) ?? 0
            const categoryWidth = xScale.bandwidth()
            const total = getCategoryTotal(category)
            const { top: categoryTopY } = getSegmentRenderBounds(
              getSegmentRenderRects(category.segments, yScale, innerHeight),
            )
            const labelY = Math.max(categoryTopY - valueLabelGap, 10)

            return (
              <text
                key={`total-${category.key}`}
                x={categoryX + categoryWidth / 2}
                y={labelY}
                fill={text_colorTextSecondary}
                fontSize={12}
                opacity={
                  activeCategoryKey === undefined ||
                  activeCategoryKey === category.key
                    ? 1
                    : 0.35
                }
                textAnchor="middle"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {(compactValueLabels ? formatCompactValueTrimmed : formatValue)(
                  total,
                  plan.yValueFormat,
                  currency,
                  locale,
                )}
              </text>
            )
          })}
          {/*
            Behind the bars, so a gridline never draws over a bar it should sit
            under. `aria-hidden` because the y axis already names these values —
            a screen reader gains nothing from six unlabelled rules.
          */}
          {showGridlines &&
            yScale
              .ticks(5)
              .map((tick) => (
                <line
                  key={`gridline-${tick}`}
                  x1={0}
                  x2={innerWidth}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke={base_colorContentBorder}
                  strokeWidth={1}
                  strokeDasharray="2 4"
                  aria-hidden="true"
                  data-testid={`chart-gridline-${tick}`}
                />
              ))}
          <AxisLeft
            scale={yScale}
            numTicks={5}
            stroke="transparent"
            tickStroke="transparent"
            tickFormat={(value) =>
              // Follows the same opt-in as the bar labels: an axis reading
              // "$1.0K" above a bar labelled "$1K" is the kind of mismatch a
              // reader notices immediately.
              (compactValueLabels
                ? formatCompactValueTrimmed
                : formatCompactValue)(
                Number(value),
                plan.yValueFormat,
                currency,
                locale,
              )
            }
            tickLabelProps={() => ({
              fill: text_colorTextSecondary,
              fontSize: axisTickFontSize,
              textAnchor: 'end',
              dy: '0.33em',
            })}
          />
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            stroke="transparent"
            tickStroke="transparent"
            tickFormat={(value) => xTickFormat(String(value))}
            tickLabelProps={() =>
              horizontalXLabels
                ? {
                    fill: text_colorTextSecondary,
                    fontSize: axisTickFontSize,
                    textAnchor: 'middle',
                    dy: '0.9em',
                  }
                : {
                    fill: text_colorTextSecondary,
                    fontSize: axisTickFontSize,
                    textAnchor: 'end',
                    dy: '0.35em',
                    dx: '-0.35em',
                    angle: diagonalXAxisLabelAngle,
                  }
            }
          />
          <line
            x1={0}
            x2={innerWidth}
            y1={zeroY}
            y2={zeroY}
            stroke={text_colorTextSecondary}
            strokeWidth={1}
            data-testid="chart-zero-baseline"
          />
          {plan.categories.flatMap((category) => {
            // Empty / all-zero periods reserve the band slot but render no bar.
            if (isCategoryEmpty(category)) return []

            const categoryX = xScale(category.label) ?? 0
            const categoryWidth = xScale.bandwidth()
            const firstSegment = category.segments[0]
            const segmentRenderRects = getSegmentRenderRects(
              category.segments,
              yScale,
              innerHeight,
            )
            const {
              top: categoryTopY,
              bottom: categoryBottomY,
              height: categoryHeight,
            } = getSegmentRenderBounds(segmentRenderRects)
            // Column-level hover target: a strip around the amount label, from
            // just above it down to the top of the bar. Big enough to be an easy
            // alternative to a tiny slice, without claiming the whole column.
            const labelY = Math.max(categoryTopY - valueLabelGap, 10)
            const amountZoneTop = Math.max(labelY - 14, 0)
            const amountZoneHeight = Math.max(categoryTopY - amountZoneTop, 0)
            const radius = Math.min(
              barCornerRadius,
              categoryWidth / 2,
              categoryHeight,
            )
            const clipPathId = `${clipPathIdPrefix}-bar-clip-${category.index}`
            const clipPathD = [
              `M ${categoryX} ${categoryBottomY}`,
              `L ${categoryX} ${categoryTopY + radius}`,
              `Q ${categoryX} ${categoryTopY} ${
                categoryX + radius
              } ${categoryTopY}`,
              `L ${categoryX + categoryWidth - radius} ${categoryTopY}`,
              `Q ${categoryX + categoryWidth} ${categoryTopY} ${
                categoryX + categoryWidth
              } ${categoryTopY + radius}`,
              `L ${categoryX + categoryWidth} ${categoryBottomY}`,
              'Z',
            ].join(' ')

            return [
              <defs key={`clip-${category.key}`}>
                <clipPath id={clipPathId}>
                  <path d={clipPathD} />
                </clipPath>
              </defs>,
              <Bar
                // Amount-zone hover target: the strip around the amount label
                // (above the bar). Hovering it opens the tooltip with the full
                // split and NO row highlighted — an easy alternative when a slice
                // is too thin to hit. Specific segments still pop their own row.
                key={`hover-${category.key}`}
                x={categoryX}
                y={amountZoneTop}
                width={categoryWidth}
                height={amountZoneHeight}
                fill="transparent"
                data-chart-stack-bar="true"
                onMouseEnter={(event) =>
                  firstSegment &&
                  setAnchoredSegment(
                    firstSegment,
                    category,
                    categoryX,
                    categoryWidth,
                    getPointerPosition(event),
                    false,
                  )
                }
                onMouseMove={(event) =>
                  firstSegment &&
                  setAnchoredSegment(
                    firstSegment,
                    category,
                    categoryX,
                    categoryWidth,
                    getPointerPosition(event),
                    false,
                  )
                }
                onMouseLeave={clearSegmentOnMouseLeave}
              />,
              <g
                key={`segments-${category.key}`}
                clipPath={`url(#${clipPathId})`}
              >
                {category.segments.map((segment) => {
                  const { y, height: barHeight } =
                    segmentRenderRects.get(segment.id) ??
                    getSegmentNaturalRect(segment, yScale)

                  return (
                    <Bar
                      key={segment.id}
                      css={style.barSegment}
                      x={categoryX}
                      y={y}
                      width={categoryWidth}
                      height={barHeight}
                      fill={segment.color}
                      stroke={base_colorRaisedSurface}
                      strokeWidth={1}
                      opacity={
                        activeCategoryKey === undefined ||
                        activeCategoryKey === segment.categoryKey
                          ? 1
                          : 0.22
                      }
                      tabIndex={-1}
                      role="graphics-symbol"
                      aria-describedby={
                        activeSegment?.segment.id === segment.id
                          ? tooltipId
                          : undefined
                      }
                      aria-label={getSegmentLabel(
                        segment,
                        plan.yValueFormat,
                        currency,
                        locale,
                      )}
                      data-testid={`chart-segment-${segment.categoryKey}-${segment.seriesKey}`}
                      data-chart-stack-segment="true"
                      data-color-token={segment.colorToken}
                      onMouseEnter={(event) =>
                        setAnchoredSegment(
                          segment,
                          category,
                          categoryX,
                          categoryWidth,
                          getPointerPosition(event),
                        )
                      }
                      onMouseMove={(event) =>
                        setAnchoredSegment(
                          segment,
                          category,
                          categoryX,
                          categoryWidth,
                          getPointerPosition(event),
                        )
                      }
                      onMouseLeave={clearSegmentOnMouseLeave}
                      onFocus={() =>
                        setAnchoredSegment(
                          segment,
                          category,
                          categoryX,
                          categoryWidth,
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          setActiveSegment(null)
                          event.currentTarget.blur()
                        }
                      }}
                      onBlur={() => setActiveSegment(null)}
                    />
                  )
                })}
              </g>,
            ]
          })}
          {plan.categories.map((category) => {
            const firstSegment = category.segments[0]
            if (!firstSegment || isCategoryEmpty(category)) return null

            const categoryX = xScale(category.label) ?? 0
            const categoryWidth = xScale.bandwidth()

            return (
              <Bar
                key={`focus-${category.key}`}
                x={categoryX}
                y={0}
                width={categoryWidth}
                height={innerHeight}
                fill="transparent"
                opacity={0}
                pointerEvents="none"
                tabIndex={0}
                role="graphics-symbol"
                aria-describedby={
                  activeCategoryKey === category.key ? tooltipId : undefined
                }
                aria-label={`${category.label} spend ${formatValue(
                  getCategoryTotal(category),
                  plan.yValueFormat,
                  currency,
                  locale,
                )}`}
                onFocus={() =>
                  setAnchoredSegment(
                    firstSegment,
                    category,
                    categoryX,
                    categoryWidth,
                    undefined,
                    false,
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setActiveSegment(null)
                    event.currentTarget.blur()
                  }
                }}
                onBlur={() => setActiveSegment(null)}
              />
            )
          })}
        </Group>
      </svg>
      {activeSegment && activeCategory
        ? createPortal(
            <div
              ref={tooltipRef}
              id={tooltipId}
              css={style.tooltip}
              role="tooltip"
              style={
                tooltipPosition
                  ? ({
                      '--tooltip-x': `${tooltipPosition.left}px`,
                      '--tooltip-y': `${tooltipPosition.top}px`,
                      position: 'fixed',
                      zIndex: 1001,
                    } as React.CSSProperties)
                  : undefined
              }
            >
              <p css={style.tooltipTitle}>
                {activeCategory.label}
                {' · '}
                {formatValue(
                  getCategoryTotal(activeCategory),
                  plan.yValueFormat,
                  currency,
                  locale,
                )}
              </p>
              <div css={style.tooltipTable} role="table">
                {tooltipSegments.map((segment) => {
                  const isActive =
                    activeSegment.highlight &&
                    segment.id === activeSegment.segment.id

                  return (
                    <div
                      key={segment.id}
                      css={style.tooltipSegmentRow(isActive)}
                      role="row"
                      data-testid={`chart-tooltip-row-${segment.seriesKey}`}
                      data-active={isActive ? 'true' : 'false'}
                    >
                      <span css={style.tooltipSeriesCell} role="cell">
                        <span
                          css={style.tooltipSwatch}
                          style={{ backgroundColor: segment.color }}
                          aria-hidden="true"
                        />
                        {segment.seriesLabel}
                      </span>
                      <span css={style.tooltipValue} role="cell">
                        {formatValue(
                          segment.value,
                          plan.yValueFormat,
                          currency,
                          locale,
                        )}
                      </span>
                    </div>
                  )
                })}
                {(activeSegment.highlight
                  ? activeSegment.segment.tooltip
                  : []
                ).map((item) => (
                  <div
                    key={item.field}
                    css={style.tooltipSegmentRow(false)}
                    role="row"
                  >
                    <span css={style.tooltipSeriesCell} role="cell">
                      {item.label}
                    </span>
                    <span css={style.tooltipValue} role="cell">
                      {formatTooltipValue(
                        item.value,
                        item.valueFormat,
                        currency,
                        locale,
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
      {showLegend && plan.series.length > 1 ? (
        <ul css={style.legend} aria-label="Chart legend">
          {plan.series.map((series) => (
            <li key={series.key} css={style.legendItem}>
              <span
                css={style.legendSwatch}
                style={{ backgroundColor: series.color }}
                aria-hidden="true"
              />
              {series.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default VisxStackedBarChart
